import { useMemo, useState } from 'react';
import { Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { MyProfileForm } from '../MyProfileForm';
import { NotificationsPanel } from '../NotificationsPanel';
import { RoomSettingsForm } from '../RoomSettingsForm';
import { FormHeader, HeaderItem } from '../FormHeader';
import { BackButton } from 'components';

import styles from './ProfileSettings.module.scss';

type Tab = 'myProfile' | 'roomSettings' | 'notifications';

type ProfileSettingsProps = {
  onBackToSettingsClick(): void;
  onDirtyChange?(dirty: boolean): void;
};

export function ProfileSettings({ onBackToSettingsClick, onDirtyChange }: ProfileSettingsProps) {
  const { t } = useTranslation('settings');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [tab, setTab] = useState<Tab | null>(isMobile ? null : 'myProfile');
  const [isMyProfileDirty, setIsMyProfileDirty] = useState(false);

  const handleProfileDirtyChange = (dirty: boolean): void => {
    setIsMyProfileDirty(dirty);
    onDirtyChange?.(dirty);
  };

  const handleTabChange = (nextTab: Tab) => {
    if (tab === 'myProfile' && isMyProfileDirty && nextTab !== 'myProfile') {
      if (!window.confirm(t('unsavedChangesPrompt'))) {
        return;
      }

      handleProfileDirtyChange(false);
    }

    setTab(nextTab);
  };

  const headerItems: HeaderItem[] = useMemo(
    () => [
      {
        text: t('profileSettings.myProfileUpper'),
        isSelected: tab === 'myProfile',
        onClick: () => handleTabChange('myProfile'),
      },
      {
        text: t('profileSettings.roomSettingsUpper'),
        isSelected: tab === 'roomSettings',
        onClick: () => handleTabChange('roomSettings'),
      },
      {
        text: t('profileSettings.notificationsUpper'),
        isSelected: tab === 'notifications',
        onClick: () => handleTabChange('notifications'),
      },
    ],
    [isMyProfileDirty, tab, t],
  );

  const getMobileHeaderName = (): string => {
    switch (tab) {
      case 'myProfile':
        return t('profileSettings.myProfileUpper');
      case 'roomSettings':
        return t('profileSettings.roomSettingsUpper');
      case 'notifications':
        return t('profileSettings.notificationsUpper');
      default:
        return t('profileSettings.profileUpper');
    }
  };

  const handleBackToProfile = (): void => {
    if (isMyProfileDirty && !window.confirm(t('unsavedChangesPrompt'))) {
      return;
    }

    handleProfileDirtyChange(false);
    setTab(null);
  };

  const handleMyProfileClick = (): void => {
    setTab('myProfile');
  };

  const handleRoomSettingsClick = (): void => {
    setTab('roomSettings');
  };

  const handleNotificationsClick = (): void => {
    setTab('notifications');
  };

  return (
    <>
      {!tab && isMobile && <BackButton text={t('profileSettings.backToSettings')} onClick={onBackToSettingsClick} />}
      {!!tab && isMobile && <BackButton text={t('profileSettings.backToProfile')} onClick={handleBackToProfile} />}

      <div className={styles.container}>
        {isMobile && (
          <>
            <FormHeader items={[{ text: getMobileHeaderName() }]} />

            {!tab && (
              <>
                <Stack className={styles.navigationItem} onClick={handleMyProfileClick}>
                  <Typography variant='body2'>{t('profileSettings.myProfile')}</Typography>
                  <ChevronRight />
                </Stack>
                <Stack className={styles.navigationItem} onClick={handleRoomSettingsClick}>
                  <Typography variant='body2'>{t('profileSettings.roomSettings')}</Typography>
                  <ChevronRight />
                </Stack>
                <Stack className={styles.navigationItem} onClick={handleNotificationsClick}>
                  <Typography variant='body2'>{t('profileSettings.notifications')}</Typography>
                  <ChevronRight />
                </Stack>
              </>
            )}

            {tab && (
              <div className={styles.main}>
                {tab === 'myProfile' && <MyProfileForm onDirtyChange={handleProfileDirtyChange} />}
                {tab === 'roomSettings' && <RoomSettingsForm />}
                {tab === 'notifications' && <NotificationsPanel />}
              </div>
            )}
          </>
        )}

        {!isMobile && (
          <>
            <FormHeader items={headerItems} />
            <div className={styles.main}>
              {tab === 'myProfile' && <MyProfileForm onDirtyChange={handleProfileDirtyChange} />}
              {tab === 'roomSettings' && <RoomSettingsForm />}
              {tab === 'notifications' && <NotificationsPanel />}
            </div>
          </>
        )}
      </div>
    </>
  );
}
